<script lang="ts">
  import { browser } from '$app/environment';
  import { page } from '$app/state';
  import { fade, scale } from 'svelte/transition';
  import { untrack } from 'svelte';
  import { PUBLIC_BRAND_NAME } from '$env/static/public';

  import { Button } from '@agapi/ui/button';
  import { Combobox } from '@agapi/ui/combobox';
  import { css } from '@agapi/ui/panda/css';
  import { Loader } from '@agapi/ui/loader';
  import { portal } from '@zag-js/svelte';

  import { getLocaleFromUrl, getAvailableMarkets, getDefaultMarket, getDefaultCountryForLanguage } from '../../utils';
  import { locationDialogStyles } from './location-dialog.styles';

  import * as m from '$lib/parachute/messages';
  import { locales, getLocale } from '$lib/parachute/runtime';
  import Logo from '$lib/assets/logo-dark.svg';

  import { Translate, GlobeSimple, ArrowRight } from 'phosphor-svelte';

  interface Props {
    clientCountry?: string;
  }

  let { clientCountry }: Props = $props();

  let isOpen = $state(false);
  let selectedCountry = $state('');
  let selectedLanguage = $state('');
  let isLoading = $state(false);
  let comboboxRef: Combobox | undefined = $state();
  let languageComboboxRef: Combobox | undefined = $state();

  const COOKIE_NAME = '__agapi_selected_country';

  // Get the classes from the styles
  const classes = locationDialogStyles.raw();

  const loadingOverlayStyles = css.raw({
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    width: '100vw',
    height: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backdropFilter: 'blur(5px)',
    backgroundColor: 'rgba(255, 255, 255, 0.6)',
    zIndex: 2147483647
  });

  /**
   * Get the country cookie value
   */
  const getCountryCookie = (): string | null => {
    if (!browser) return null;
    const cookies = document.cookie.split(';');
    const cookie = cookies.find(c => c.trim().startsWith(`${COOKIE_NAME}=`));
    return cookie ? cookie.split('=')[1] : null;
  };

  /**
   * Set the country cookie
   */
  const setCountryCookie = (country: string): void => {
    if (!browser) return;
    // Set cookie that never expires
    document.cookie = `${COOKIE_NAME}=${country}; path=/; max-age=31536000; SameSite=Lax`;
  };

  /**
   * Handle country query parameter
   * If ?country=XX is present, save it and don't show dialog
   */
  const handleCountryQueryParam = (): boolean => {
    if (!browser) return false;

    const urlParams = new URLSearchParams(page.url.search);
    const countryParam = urlParams.get('country');

    if (countryParam) {
      const countryCode = countryParam.toUpperCase();
      const availableMarkets = getAvailableMarkets();

      // Check if the country is valid and available
      if (availableMarkets.includes(countryCode as any)) {
        // Save the country choice in cookie
        setCountryCookie(countryCode);
        return true; // Indicates we handled the query param
      }
    }

    return false;
  };

  /**
   * Determine if we should show the dialog
   */
  const shouldShowDialog = (): boolean => {
    if (!browser) return false;

    // First check if country is set via query parameter
    if (handleCountryQueryParam()) {
      return false; // Don't show dialog if country was set via query param
    }

    const savedCountry = getCountryCookie();
    const currentLocale = getLocaleFromUrl(page.url);
    const currentCountry = currentLocale.country;

    // Don't show if user has already selected a country
    if (savedCountry) return false;

    // Show if we have a detected country that differs from current market
    if (clientCountry && clientCountry !== currentCountry) {
      return true;
    }

    // Show if no country detected and we're on default market (first visit)
    // Use language-specific default country for comparison
    if (!clientCountry) {
      const languageDefaultCountry = getDefaultCountryForLanguage(currentLocale.language);
      // Show dialog if current country differs from what would be expected for this language
      if (currentCountry !== languageDefaultCountry) {
        return true;
      }
    }

    return false;
  };

  /**
   * Initialize the dialog state
   */
  const initializeDialog = (): void => {
    if (shouldShowDialog()) {
      const currentLocale = getLocaleFromUrl(page.url);

      // Auto-select detected country or default to language-specific country
      const detectedCountry = clientCountry
        ? clientCountry.toUpperCase()
        : getDefaultCountryForLanguage(currentLocale.language);

      // Ensure the detected country is in available markets
      const availableMarkets = getAvailableMarkets();
      selectedCountry = availableMarkets.includes(detectedCountry as any)
        ? detectedCountry
        : getDefaultMarket();

      // Set current language as selected
      selectedLanguage = currentLocale.language;

      isOpen = true;
    }
  };

  /**
   * Handle language selection - only update state
   */
  const handleLanguageChange = (lang: string): void => {
    selectedLanguage = lang;
  };

  /**
   * Handle navigation with selected language and country
   */
  const handleContinueToShop = (): void => {
    if (!selectedLanguage) return;

    isLoading = true;

    // Save the selected country in cookie if one is selected
    if (selectedCountry) {
    setCountryCookie(selectedCountry);
    }

    // Construct the new URL with selected language
    const languagePath = locales.includes(selectedLanguage)
      ? selectedLanguage === 'sv' ? '' : selectedLanguage
      : '';
    const url = new URL(page.url.origin + '/' + languagePath);

    // Add country parameter if selected and not SE
    if (selectedCountry && selectedCountry.toUpperCase() !== 'SE') {
      url.searchParams.set('country', selectedCountry.toLowerCase());
    } else {
      // For SE (default market), ensure no country parameter is set
      url.searchParams.delete('country');
    }

    // Redirect to the new URL
    location.replace(url.toString());
  };

  /**
   * Close the dialog
   */
  const closeDialog = (): void => {
    isOpen = false;
  };

  /**
   * Handle escape key
   */
  const handleKeydown = (event: KeyboardEvent): void => {
    if (event.key === 'Escape') {
      closeDialog();
    }
  };

  // Initialize on mount
  $effect(() => {
    if (browser) {
      initializeDialog();
    }
  });

  // Set combobox value when ref and selectedCountry are available
  $effect.pre(() => {
    if (comboboxRef && selectedCountry) {
      untrack(() => {
        comboboxRef?.setValue([selectedCountry]);
      });
    }
  });

  // Set language combobox value when ref and selectedLanguage are available
  $effect.pre(() => {
    if (languageComboboxRef && selectedLanguage) {
      untrack(() => {
        languageComboboxRef?.setValue([selectedLanguage]);
      });
    }
  });

  // Get available countries for the combobox
  const countryOptions = getAvailableMarkets().map((countryCode) => ({
    value: countryCode,
    label: `${m[`${countryCode?.toLowerCase?.()}Country`]?.() ?? countryCode} / ${m[`${countryCode?.toLowerCase?.()}CountryEnglish`]?.() ?? countryCode}`
  }));

  // Get available languages for the combobox
  const languageOptions = locales.map((lang) => ({
    value: lang,
    label: m[`${lang}Language`]?.() ?? `${lang} Language`
  }));

  // Get the country name for the selected country (bilingual format)
  const getSelectedCountryName = () => {
    if (!selectedCountry) return '';
    const localName = m[`${selectedCountry?.toLowerCase?.()}Country`]?.() ?? selectedCountry;
    const englishName = m[`${selectedCountry?.toLowerCase?.()}CountryEnglish`]?.() ?? selectedCountry;
    return `${localName} / ${englishName}`;
  };

  // Get the country name for the selected country (bilingual format)
  const getSelectedCountryNameSimple = () => {
    if (!selectedCountry) return '';
    return m[`${selectedCountry?.toLowerCase?.()}Country`]?.() ?? selectedCountry;

  };
</script>

<svelte:window on:keydown={handleKeydown} />

{#if isOpen}
  <!-- Backdrop -->
  <div
    class={css(classes.backdrop)}
    data-part="backdrop"
    tabindex={1}
    transition:fade={{ duration: 200 }}
    role="dialog"
    aria-modal="true"
    aria-labelledby="location-dialog-title"
    aria-describedby="location-dialog-description"
  >
    <!-- Dialog Content -->
    <div
      class={css(classes.dialogContent)}
      data-part="dialog-content"
      transition:scale={{ duration: 200, start: 0.95 }}
    >
      <div class={css(classes.container)} data-part="container">
        <!-- Logo -->
        <div class={css(classes.logo)} data-part="logo">
          <img src={Logo} class={css({ maxWidth: "160px" })} alt={`${PUBLIC_BRAND_NAME} Logo`} />
        </div>

        <!-- <h2 id="location-dialog-title" class={css(classes.title)} data-part="title">
          {m.whereAreYouShoppingFrom?.() ?? 'Where are you shopping from?'}
        </h2> -->

        <!-- <p id="location-dialog-description" class={css(classes.description)} data-part="description">
          {m.chooseYourLanguageAndCountry?.() ?? 'Choose your language and country to get the best shopping experience.'}
        </p> -->

        <!-- Language Selection -->
        <div class={css(classes.languageSelector)} data-part="language-selector">
          <h5 class={css(classes.secondaryLabel, {lineHeight: '1.2'})}>
            <Translate>
              <title>{m.iconTranslate()}</title>
            </Translate> {m.chooseLangAll?.() ?? 'Choose language'}
          </h5>
          <Combobox
            bind:this={languageComboboxRef}
            items={languageOptions}
            onValueChange={({ value: [languageCode] }) => {
              handleLanguageChange(languageCode);
            }}
            inputStyles={{
              backgroundColor: 'surface.default.background',
            }}

            positionerStyles={classes.positioner}
            placeholder={m.selectLanguage?.() ?? 'Select language'}
            clearOnOpen={true}
          />
        </div>

        <!-- Country Selection -->
        <div class={css(classes.countrySelector)} data-part="country-selector">
          <h5 class={css(classes.secondaryLabel)}>
            <GlobeSimple>
              <title>{m.iconCountry()}</title>
            </GlobeSimple> {m.selectCountry?.() ?? 'Country'}
          </h5>
          <Combobox
            bind:this={comboboxRef}
            items={countryOptions}
            onValueChange={({ value: [countryCode] }) => {
              selectedCountry = countryCode;
            }}
            inputStyles={{
              backgroundColor: 'surface.default.background',
            }}
            positionerStyles={classes.positioner}
            placeholder={m.selectCountry?.() ?? 'Select country'}
            clearOnOpen={true}
          />
        </div>

        <Button
          css={classes.shopButton}
          data-part="shop-button"
          onclick={handleContinueToShop}
          disabled={!selectedLanguage || isLoading}
        >
          {isLoading ?
            (m.loading?.() ?? 'Loading...') :
            (m.continueToShop?.() ?? 'Continue to shop')
          }
          <ArrowRight>
            <title>{m.iconForward()}</title>
          </ArrowRight>
        </Button>
      </div>
    </div>
  </div>
{/if}

{#if isLoading}
  <div use:portal class={css(loadingOverlayStyles)}>
    <Loader type="spinner" size="2xl" color="primary.500" />
  </div>
{/if}